/**
 * Cash position data access (FSD Part 3, 3.2.1 S05: FIN-T01-R016..R018, R021;
 * V046..V049, V052, V055; AC032, AC033, AC036).
 *
 * Thin wrappers over the generated client, owned by the position screens
 * (P23-PLAN SUI+: nothing is added to `treasury/c4/endpoints.ts`, and the
 * position screens no longer read the hand-written `c4/types` shapes, which
 * predate `calcVersion`). Every amount is the server's decimal string; nothing
 * here, or in any screen above it, adds one figure to another.
 *
 * Cache keys share the `['treasury', 'positions']` prefix the diff view's keys
 * use, so invalidating that prefix after a publish refreshes every position
 * screen at once.
 */

import {
  consolGroupsList,
  fxCurrenciesList,
  orgCompaniesList,
  treasuryPositionsCurrent,
  treasuryPositionsGet,
  treasuryPositionsGroup,
  treasuryPositionsList,
  treasuryPositionsPreview,
  treasuryPositionsPublish,
  treasuryPositionsRateToleranceSet,
  treasuryPositionsRepublish,
  treasuryPositionsVerify,
  treasuryPositionsVersions,
  treasurySettingsGet,
  type Company,
  type ConsolGroupDTO,
  type CurrencyDTO,
  type PageTreasuryPositionRunDTO,
  type PublishPositionRequest,
  type RepublishPositionRequest,
  type TreasuryGroupPositionDTO,
  type TreasuryPositionDetailDTO,
  type TreasuryPositionRunDTO,
  type TreasuryPositionToleranceDTO,
  type TreasuryPositionVerifyDTO,
  type TreasurySettingsDTO,
} from '@erpx/api'
import { toProblemDetails } from '@erpx/ui'

import { api, rawRequest } from '@/api/client'
import { MAX_LIMIT } from '@/composables/useTableUrlState'

const MAX_PAGES = 10

/** The permission `GET /treasury-positions/group` declares (not yet in `permissions.ts`). */
export const POSITION_GROUP_READ = 'treasury.position.group.read'
/** The permission `GET /consol-groups` declares: the perimeter picker. */
export const CONSOL_GROUP_READ = 'consol.group.read'
/** The permission `GET /currencies` declares: the reporting-currency picker. */
export const CURRENCY_READ = 'fx.currency.read'

export const POSITION_ERROR_CODES = {
  notPublished: 'ERR_POSITION_NOT_PUBLISHED',
  staleRate: 'ERR_POSITION_STALE_RATE',
  rowVersion: 'ERR_ROW_VERSION',
} as const

export interface PositionListFilters {
  companyId: number
  from: string
  to: string
  includeSuperseded: boolean
  cursor: string
  limit: number
}

export interface GroupPositionFilters {
  /** 0 when the perimeter is declared by legal entities instead. */
  perimeterId: number
  /** Sorted, de-duplicated legal entity ids; empty with a perimeter. */
  companyIds: readonly number[]
  /** '' means the perimeter's presentation currency (or the entities' shared one). */
  reportingCurrency: string
  /** '' means today on the tenant's clock. */
  asOf: string
  source: 'live' | 'published'
}

export const POSITIONS_KEYS = {
  all: ['treasury', 'positions'] as const,
  legalEntities: ['treasury', 'positions', 'legal-entities'] as const,
  perimeters: ['treasury', 'positions', 'perimeters'] as const,
  currencies: ['treasury', 'positions', 'currencies'] as const,
  list: (filters: PositionListFilters) => ['treasury', 'positions', 'list', filters] as const,
  current: (companyId: number) => ['treasury', 'positions', 'current', companyId] as const,
  detail: (id: number) => ['treasury', 'positions', 'detail', id] as const,
  versions: (id: number) => ['treasury', 'positions', 'versions', id] as const,
  preview: (companyId: number, asOf: string) =>
    ['treasury', 'positions', 'preview', companyId, asOf] as const,
  group: (filters: GroupPositionFilters) => ['treasury', 'positions', 'group', filters] as const,
  settings: (companyId: number) => ['treasury', 'positions', 'settings', companyId] as const,
} as const

/* ---------------------------------------------------------------- pickers */

/** Every active legal entity the caller may see, walked to the end of the cursor. */
export async function listLegalEntities(): Promise<Company[]> {
  const items: Company[] = []
  let cursor = ''
  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await orgCompaniesList(api(), {
      limit: MAX_LIMIT,
      isActive: true,
      ...(cursor === '' ? {} : { cursor }),
    })
    items.push(...result.items)
    if (!result.hasMore || result.nextCursor === null || result.nextCursor === '') break
    cursor = result.nextCursor
  }
  return items
}

/** Every active consolidation group: each is a declarable perimeter. */
export async function listPerimeters(): Promise<ConsolGroupDTO[]> {
  const items: ConsolGroupDTO[] = []
  let cursor = ''
  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await consolGroupsList(api(), {
      limit: MAX_LIMIT,
      isActive: true,
      ...(cursor === '' ? {} : { cursor }),
    })
    items.push(...result.items)
    if (!result.hasMore || result.nextCursor === null || result.nextCursor === '') break
    cursor = result.nextCursor
  }
  return items
}

/** Every active currency, for the reporting-currency picker. */
export async function listCurrencies(): Promise<CurrencyDTO[]> {
  const items: CurrencyDTO[] = []
  let cursor = ''
  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await fxCurrenciesList(api(), {
      limit: MAX_LIMIT,
      activeOnly: true,
      ...(cursor === '' ? {} : { cursor }),
    })
    items.push(...result.items)
    if (!result.hasMore || result.nextCursor === null || result.nextCursor === '') break
    cursor = result.nextCursor
  }
  return items
}

/* -------------------------------------------------------- published runs */

/** Published runs of one legal entity. Never called without one. */
export function listPositions(filters: PositionListFilters): Promise<PageTreasuryPositionRunDTO> {
  return treasuryPositionsList(api(), {
    companyId: filters.companyId,
    limit: filters.limit,
    ...(filters.from === '' ? {} : { from: filters.from }),
    ...(filters.to === '' ? {} : { to: filters.to }),
    ...(filters.includeSuperseded ? { includeSuperseded: true } : {}),
    ...(filters.cursor === '' ? {} : { cursor: filters.cursor }),
  })
}

/** The latest published run; 404 ERR_POSITION_NOT_PUBLISHED when there is none. */
export function currentPosition(companyId: number): Promise<TreasuryPositionDetailDTO> {
  return treasuryPositionsCurrent(api(), { companyId })
}

export function getPosition(id: number): Promise<TreasuryPositionDetailDTO> {
  return treasuryPositionsGet(api(), { id: String(id) })
}

/** Every version of one run's date: the restatement history (V055). */
export function positionVersions(id: number): Promise<TreasuryPositionRunDTO[]> {
  return treasuryPositionsVersions(api(), { id: String(id) })
}

/** The live, unpublished computation. */
export function previewPosition(companyId: number, asOf: string): Promise<TreasuryPositionDetailDTO> {
  return treasuryPositionsPreview(api(), { companyId, ...(asOf === '' ? {} : { asOf }) })
}

export function publishPosition(
  body: PublishPositionRequest,
  idempotencyKey: string,
): Promise<TreasuryPositionDetailDTO> {
  return treasuryPositionsPublish(api(), body, idempotencyKey)
}

export function republishPosition(
  id: number,
  body: RepublishPositionRequest,
  idempotencyKey: string,
): Promise<TreasuryPositionDetailDTO> {
  return treasuryPositionsRepublish(api(), { id: String(id) }, body, idempotencyKey)
}

export function verifyPosition(id: number): Promise<TreasuryPositionVerifyDTO> {
  return treasuryPositionsVerify(api(), { id: String(id) })
}

/** The published run as CSV. A stream, so it goes through `rawRequest`. */
export async function exportPosition(id: number): Promise<Blob> {
  const response = await rawRequest('GET', `/api/v1/treasury-positions/${id}/export`)
  return response.blob()
}

/* ------------------------------------------------------------ the group */

export function groupPosition(filters: GroupPositionFilters): Promise<TreasuryGroupPositionDTO> {
  return treasuryPositionsGroup(api(), {
    ...(filters.perimeterId > 0 ? { perimeterId: filters.perimeterId } : {}),
    ...(filters.companyIds.length > 0 ? { companyIds: filters.companyIds.join(',') } : {}),
    ...(filters.reportingCurrency === '' ? {} : { reportingCurrency: filters.reportingCurrency }),
    ...(filters.asOf === '' ? {} : { asOf: filters.asOf }),
    source: filters.source,
  })
}

/* ------------------------------------------------------- rate tolerance */

/** The settings row: its `rowVersion` guards the tolerance write. */
export function getTreasurySettings(companyId: number): Promise<TreasurySettingsDTO> {
  return treasurySettingsGet(api(), { companyId: String(companyId) })
}

/**
 * Declare how many whole DAYS old a translation rate may be before a translated
 * position cannot be published (V049). Days, 0 to 90 — not a rate, not a percent.
 */
export function setRateTolerance(
  companyId: number,
  days: number,
  rowVersion: number,
  idempotencyKey: string,
): Promise<TreasuryPositionToleranceDTO> {
  return treasuryPositionsRateToleranceSet(api(), { companyId, days, rowVersion }, idempotencyKey)
}

/* --------------------------------------------------------------- errors */

export function hasCode(error: unknown, code: string): boolean {
  return toProblemDetails(error)?.code === code
}

/** "Nothing has ever been published": an empty state, never a failure. */
export function isNotPublished(error: unknown): boolean {
  return hasCode(error, POSITION_ERROR_CODES.notPublished)
}

/** V049's refusal: a translation rate older than the declared tolerance. */
export function isStaleRate(error: unknown): boolean {
  return hasCode(error, POSITION_ERROR_CODES.staleRate)
}

/**
 * The `treasury.basis.*` label key of a wire basis: the wire says
 * `bank_booked` / `value_dated`, the catalogue says `bankBooked` / `valueDated`.
 */
export function basisLabelKey(basis: string): string {
  if (basis === 'bank_booked') return 'treasury.basis.bankBooked'
  if (basis === 'value_dated') return 'treasury.basis.valueDated'
  return `treasury.basis.${basis}`
}

/**
 * An ownership percent as the server states it ("60.0000"), without the
 * trailing zeros. String work only: a percent is never parsed into a number.
 */
export function trimPct(value: string): string {
  if (!value.includes('.')) return value
  return value.replace(/0+$/, '').replace(/\.$/, '')
}
